import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api/api';
import type {
  EntityCreateInput,
  RelationshipInput,
  RelationshipEndInput,
} from '@/lib/api/domains/entities';
import type { EntityId, RelationshipId } from '@/lib/api/ids';
import { keys } from '@/lib/query-keys';

/*
 * Mutations for organizations and relationships (E02.F04). Pessimistic:
 * mutate → invalidate. NO optimistic updates on entity/relationship mutations
 * (AGENTS.md, product non-negotiables) — the server is the authority on what
 * was written and what is in force, so the UI re-reads rather than guessing.
 *
 * There is no idempotency key: no F04 route is a money command (design §4 —
 * "no money command exists in F04"); the create path returns the existing
 * record on a repeat (R7, R45), which is the server's concern, not a header
 * the FE stamps.
 */

export function useCreateEntity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EntityCreateInput) => api.entities.create(input),
    onSuccess: () => {
      // A new organization changes what lists show; the prefix cascade reaches
      // lists and details alike.
      void queryClient.invalidateQueries({ queryKey: keys.entities.all() });
    },
  });
}

export function useCreateRelationship(entityId: EntityId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RelationshipInput) =>
      api.entities.createRelationship(entityId, input),
    onSuccess: () => {
      // The relationship shows on this entity's detail (both directions) and may
      // show on the other party's; invalidate the whole entities tree so both
      // sides re-read.
      void queryClient.invalidateQueries({ queryKey: keys.entities.all() });
    },
  });
}

export function useEndRelationship(entityId: EntityId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      relationshipId: RelationshipId;
      body: RelationshipEndInput;
    }) => api.entities.endRelationship(args.relationshipId, args.body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: keys.entities.detail(entityId),
      });
    },
  });
}
